import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Button,
  ConfirmDialog,
  EmptyState,
  ItemTable,
  ItemTableSearch,
  ItemTableRow,
  ItemTableCell,
  ItemTableAvatar,
  ItemTableTitle,
  ItemTableSubtitle,
  ItemTableActions,
  ItemTableLoading,
  ItemTableEmpty,
  Badge,
  Dropdown,
  Tabs,
  type Tab,
} from "@ui";
import {
  useAgents,
  useCreateAgent,
  useDeleteAgent,
  useUpdateAgent,
  useReorderAgents,
  usePreviewTargets,
  type Agent,
  type PreviewTarget,
} from "@agentic_system/api/agent";
import { useNavigate } from "react-router-dom";
import { Bot, Plus, Pencil, Trash2, FileText, Wrench, User, Globe, GripVertical, Eye, QrCode, ExternalLink, EyeOff } from "lucide-react";
import Sortable from "sortablejs";

type BadgeFilterKey =
  | "published"
  | "unpublished"
  | "schema"
  | "tools"
  | "documents"
  | "qr_code";

interface BadgeFilterOption {
  key: BadgeFilterKey;
  label: string;
}

const BADGE_FILTERS: BadgeFilterOption[] = [
  { key: "published", label: "Publié" },
  { key: "unpublished", label: "Non publié" },
  { key: "schema", label: "Schéma" },
  { key: "tools", label: "Outils" },
  { key: "documents", label: "Docs" },
  { key: "qr_code", label: "QR Code" },
];

async function openDrawer(url: string) {
  const response = await fetch(url, {
    headers: { Accept: "text/vnd.turbo-stream.html" },
  });
  const html = await response.text();
  (window as any).Turbo.renderStreamMessage(html);
}

function PreviewSelect({ agent }: { agent: Agent }) {
  const profileClass = agent.profile_class;
  if (!profileClass) return null;

  const { data: targets } = usePreviewTargets(profileClass);

  const drawerUrl = (targetId: number) =>
    `/system/profile_entrypoint/drawer?agent_id=${agent.id}&profile_type=${encodeURIComponent(profileClass)}&profile_id=${targetId}`;

  const items = (targets ?? []).map((t) => ({
    label: t.name,
    onClick: () => openDrawer(drawerUrl(t.id)),
  }));

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown
        trigger={
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded-md bg-white text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <Eye className="w-3 h-3" />
            <span>Tester{targets ? ` (${targets.length})` : ""}</span>
          </button>
        }
        items={items}
      />
    </div>
  );
}

const SYSTEM_PROMPT_TABS: Tab[] = [
  {
    to: "/system/agentic/agents/connected-user",
    label: "Utilisateur connecté",
    icon: User,
  },
  {
    to: "/system/agentic/agents/profile-entrypoint",
    label: "Point d'entrée profil",
    icon: Globe,
  },
];

function truncate(text: string | null, maxLength: number): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

const PROFILE_LABEL_MAP: Record<string, string> = {
  "Profile::Candidat": "Candidat",
  "Profile::Enseigne": "Enseigne",
  "Profile::Immo": "Bailleur",
  "Profile::Partner": "Partenaire",
  "Profile::Union": "Union",
  "Profile::Ville": "Ville",
  "Location": "Emplacement",
  "PointOfSale": "Point de vente",
};

function getProfileLabel(profileClass: string | null): string | null {
  if (!profileClass) return null;
  return PROFILE_LABEL_MAP[profileClass] || null;
}

/** Display order for profile groups — "all profiles" first, then by type */
const PROFILE_GROUP_ORDER: (string | null)[] = [
  null,
  "Profile::Candidat",
  "Profile::Enseigne",
  "Profile::Immo",
  "Profile::Partner",
  "Profile::Union",
  "Profile::Ville",
];

const ENTRYPOINT_GROUP_ORDER: (string | null)[] = [
  ...PROFILE_GROUP_ORDER,
  "Location",
  "PointOfSale",
];

function getGroupLabel(profileClass: string | null): string {
  if (!profileClass) return "Tous les profils";
  return PROFILE_LABEL_MAP[profileClass] || profileClass;
}

interface AgentGroup {
  key: string;
  profileClass: string | null;
  label: string;
  agents: Agent[];
}

interface AgentsProps {
  systemPromptType: string;
}

export function Agents({ systemPromptType }: AgentsProps) {
  const { data: allAgents, isLoading } = useAgents();

  // Filter agents by system_prompt_type
  const agents = useMemo(() => {
    if (!allAgents) return undefined;
    return allAgents.filter((a) => a.system_prompt_type === systemPromptType);
  }, [allAgents, systemPromptType]);
  const navigate = useNavigate();
  const createAgent = useCreateAgent();
  const deleteAgent = useDeleteAgent();
  const updateAgent = useUpdateAgent();
  const reorderAgents = useReorderAgents();

  const openAgent = useCallback((agent: Agent) => {
    navigate(`/system/agentic/agents/${agent.id}`);
  }, [navigate]);
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeBadgeFilters, setActiveBadgeFilters] = useState<BadgeFilterKey[]>([]);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const sortablesRef = useRef<Record<string, Sortable>>({});

  const agentMatchesBadgeFilter = useCallback((agent: Agent, filter: BadgeFilterKey) => {
    switch (filter) {
      case "published":
        return !!agent.published_at;
      case "unpublished":
        return !agent.published_at;
      case "schema":
        return !!agent.active_schema;
      case "tools":
        return agent.tools.length > 0;
      case "documents":
        return agent.documents.length > 0;
      case "qr_code":
        return !!agent.qr_code;
      default:
        return true;
    }
  }, []);

  const toggleBadgeFilter = useCallback((filter: BadgeFilterKey) => {
    setActiveBadgeFilters((current) =>
      current.includes(filter)
        ? current.filter((key) => key !== filter)
        : [...current, filter],
    );
  }, []);

  // Client-side filtering
  const filteredAgents = useMemo(() => {
    if (!agents) return [];
    const query = searchQuery.trim().toLowerCase();

    return agents.filter((agent) => {
      const matchesSearch = !query || (
        agent.label.toLowerCase().includes(query) ||
        agent.user_facing_question?.toLowerCase().includes(query) ||
        agent.system_prompt?.toLowerCase().includes(query)
      );

      if (!matchesSearch) return false;
      if (activeBadgeFilters.length === 0) return true;

      return activeBadgeFilters.every((filter) => agentMatchesBadgeFilter(agent, filter));
    });
  }, [activeBadgeFilters, agentMatchesBadgeFilter, agents, searchQuery]);

  // Group filtered agents by profile_class
  const groupOrder = systemPromptType === "profile_entrypoint"
    ? ENTRYPOINT_GROUP_ORDER
    : PROFILE_GROUP_ORDER;

  const groupedAgents = useMemo((): AgentGroup[] => {
    const groups = new Map<string | null, Agent[]>();

    for (const agent of filteredAgents) {
      const key = agent.profile_class;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(agent);
    }

    return groupOrder
      .filter((key) => groups.has(key))
      .map((profileClass) => ({
        key: profileClass ?? "__all__",
        profileClass,
        label: getGroupLabel(profileClass),
        agents: groups.get(profileClass)!,
      }));
  }, [filteredAgents, groupOrder]);

  // Initialize Sortable for each group (drag-and-drop reordering within groups)
  useEffect(() => {
    // Cleanup existing instances
    Object.values(sortablesRef.current).forEach((s) => s.destroy());
    sortablesRef.current = {};

    if (!agents || agents.length === 0 || searchQuery.trim()) return;

    for (const group of groupedAgents) {
      const el = groupRefs.current[group.key];
      if (!el || group.agents.length < 2) continue;

      sortablesRef.current[group.key] = Sortable.create(el, {
        animation: 150,
        handle: "[data-drag-handle]",
        onEnd: async (evt) => {
          const oldIndex = evt.oldIndex;
          const newIndex = evt.newIndex;

          if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) return;

          const groupAgents = [...group.agents];
          const [movedAgent] = groupAgents.splice(oldIndex, 1);
          groupAgents.splice(newIndex, 0, movedAgent);

          const agentsWithPositions = groupAgents.map((agent, index) => ({
            id: agent.id,
            position: index,
          }));

          await reorderAgents.mutateAsync(agentsWithPositions);
        },
      });
    }

    return () => {
      Object.values(sortablesRef.current).forEach((s) => s.destroy());
      sortablesRef.current = {};
    };
  }, [groupedAgents, agents, reorderAgents, searchQuery]);

  const handleNewAgent = () => {
    createAgent.mutate(
      { label: "New Agent", system_prompt_type: systemPromptType },
      {
        onSuccess: (agent) => {
          openAgent(agent);
        },
      },
    );
  };

  const handleDelete = useCallback((agent: Agent) => {
    setDeleteTarget(agent);
  }, []);

  const handleTogglePublish = useCallback(async (agent: Agent) => {
    await updateAgent.mutateAsync({
      id: agent.id,
      input: {
        published_at: agent.published_at ? null : new Date().toISOString(),
      },
    });
  }, [updateAgent]);

  const confirmDelete = useCallback(async () => {
    if (deleteTarget) {
      await deleteAgent.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteAgent]);

  const hasAgents = (agents?.length ?? 0) > 0;
  const showDragHandle = !searchQuery.trim();

  const renderAgentRow = (agent: Agent) => {
    const profileLabel = getProfileLabel(agent.profile_class);
    return (
      <ItemTableRow
        key={agent.id}
        onClick={() => openAgent(agent)}
        className="grid-cols-[auto_1fr_auto_auto]"
      >
        {showDragHandle && (
          <ItemTableCell>
            <div
              data-drag-handle
              className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 p-1"
            >
              <GripVertical className="w-4 h-4" />
            </div>
          </ItemTableCell>
        )}
        <ItemTableCell>
          <ItemTableAvatar icon={Bot} />
          <div className="min-w-0">
            <ItemTableTitle>{agent.label}</ItemTableTitle>
            {agent.user_facing_question && agent.user_facing_question !== agent.label && (
              <ItemTableSubtitle>
                {agent.user_facing_question}
              </ItemTableSubtitle>
            )}
            {agent.system_prompt && (
              <p className="text-xs text-slate-400 mt-0.5 font-mono truncate">
                {truncate(agent.system_prompt, 80)}
              </p>
            )}
          </div>
        </ItemTableCell>
        <ItemTableCell>
          <div className="flex items-center gap-2">
            <Badge
              label={agent.published_at ? "Publié" : "Non publié"}
              color={agent.published_at ? "green" : "default"}
              size="sm"
            />
            {agent.active_schema && (
              <Badge
                label={`Schéma v${agent.active_schema.version}`}
                color="indigo"
                size="sm"
              />
            )}
            {profileLabel && (
              <Badge
                label={profileLabel}
                color="purple"
                size="sm"
                icon={User}
              />
            )}
            {agent.tools.length > 0 && (
              <Badge
                label={`${agent.tools.length} outil${agent.tools.length > 1 ? "s" : ""}`}
                color="blue"
                size="sm"
                icon={Wrench}
              />
            )}
            {agent.documents.length > 0 && (
              <Badge
                label={`${agent.documents.length} doc${agent.documents.length > 1 ? "s" : ""}`}
                color="green"
                size="sm"
                icon={FileText}
              />
            )}
            {agent.qr_code && (
              <Badge
                label="QR Code"
                color="yellow"
                size="sm"
                iconLeft={QrCode}
              />
            )}
            {systemPromptType === "profile_entrypoint" && (
              <PreviewSelect agent={agent} />
            )}
            {systemPromptType !== "profile_entrypoint" && (() => {
              const hasMarketing = !!(agent.marketing_title?.trim() && agent.marketing_content?.trim());
              return (
                <div className="relative group" onClick={(e) => e.stopPropagation()}>
                  {hasMarketing && agent.slug ? (
                    <a
                      href={`/agents/${agent.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:text-default-600 hover:bg-slate-100 transition-colors"
                      title="Voir la page marketing"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-300 cursor-not-allowed"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
                        <div className="bg-slate-900/85 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                          Ajouter les infos marketing pour accéder à la preview
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </ItemTableCell>
        <ItemTableCell>
          <ItemTableActions
            items={[
              {
                icon: <Pencil className="w-4 h-4" />,
                label: "Modifier",
                onClick: () => openAgent(agent),
              },
              {
                icon: agent.published_at ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />,
                label: agent.published_at ? "Dépublier" : "Publier",
                onClick: () => handleTogglePublish(agent),
              },
              {
                icon: <Trash2 className="w-4 h-4" />,
                label: "Supprimer",
                onClick: () => handleDelete(agent),
                variant: "destructive",
              },
            ]}
          />
        </ItemTableCell>
      </ItemTableRow>
    );
  };

  return (
    <Page
      data-testid="agents"
      title={
        <div className="flex items-center gap-4">
          <span>Agents</span>
          <Tabs tabs={SYSTEM_PROMPT_TABS} />
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button
            icon={Plus}
            onClick={handleNewAgent}
            loading={createAgent.isPending}
          >
            Nouvel agent
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <ItemTable>
          <ItemTableLoading rows={4} />
        </ItemTable>
      ) : hasAgents ? (
        <ItemTable>
          <ItemTableSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Rechercher des agents..."
          />
          <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
            {BADGE_FILTERS.map((filter) => {
              const active = activeBadgeFilters.includes(filter.key);
              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => toggleBadgeFilter(filter.key)}
                  className={[
                    "inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    active
                      ? "border-blue-200 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
          {groupedAgents.length > 0 ? (
            groupedAgents.map((group) => (
              <div key={group.key}>
                <div className="px-6 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {group.label}
                  </span>
                  <span className="text-xs text-slate-400 tabular-nums">
                    ({group.agents.length})
                  </span>
                </div>
                <div
                  ref={(el) => { groupRefs.current[group.key] = el; }}
                  className="divide-y divide-slate-200"
                >
                  {group.agents.map(renderAgentRow)}
                </div>
              </div>
            ))
          ) : (
            <ItemTableEmpty
              title="Aucun agent trouvé"
              description={
                searchQuery.trim()
                  ? `Aucun agent ne correspond à "${searchQuery}"`
                  : "Aucun agent ne correspond aux filtres sélectionnés"
              }
            />
          )}
        </ItemTable>
      ) : (
        <EmptyState
          icon={Bot}
          title="Aucun agent"
          description="Créez votre premier agent IA pour commencer."
          action={
            <Button
              icon={Plus}
              onClick={handleNewAgent}
              loading={createAgent.isPending}
            >
              Créer un agent
            </Button>
          }
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Supprimer l'agent"
        subtitle={deleteTarget?.label}
        description="Êtes-vous sûr de vouloir supprimer cet agent ? Cette action est irréversible."
        confirm={{
          label: "Supprimer",
          onClick: confirmDelete,
        }}
        variant="destructive"
        loading={deleteAgent.isPending}
      />
    </Page>
  );
}
